import { useNavigate } from 'react-router'
import { useGame } from '../GameContext'
import { COLOR_HEX, SCORE_BANDS } from '../constants'
import { Button, GolfBall, WashiTape } from '../components/ui'
import { Podium } from './Podium'
import { RotateCcw, Share2, Trophy } from 'lucide-react'
import { toast } from 'sonner'

export default function Results() {
  const navigate = useNavigate()
  const { gameState, resetGame } = useGame()

  if (!gameState.course || gameState.players.length === 0) {
    navigate('/')
    return null
  }

  const players = [...gameState.players]
    .map((player) => {
      const total = gameState.holes.reduce((sum, hole) => {
        const score = hole.scores.find((s) => s.playerId === player.id)
        return sum + (score?.strokes || 0)
      }, 0)
      return {
        id: player.id,
        name: player.name,
        total,
        selectedColor: player.selectedColor,
      }
    })
    .sort((a, b) => a.total - b.total)

  const share = () => {
    const lines = players.map((p, i) => `${i + 1}. ${p.name}: ${p.total}`).join('\n')
    const text = `🏌️ Mini Golf Results at ${gameState.course.name}!\n\n${lines}\n\n🏆 Winner: ${players[0].name}!`
    if (navigator.share) {
      navigator.share({ title: 'Mini Golf Scores', text }).catch(() => {})
    } else {
      navigator.clipboard.writeText(text)
      toast.success('Results copied to clipboard!')
    }
  }

  const replay = () => {
    resetGame()
    navigate('/')
  }

  const totalPar =
    gameState.course.holesData?.reduce((sum, h) => sum + (h.par ?? 3), 0) ??
    gameState.course.holes * 3

  return (
    <div className="min-h-screen flex flex-col py-6">
      <div className="text-center mb-4 relative">
        <WashiTape className="left-1/2 -translate-x-1/2 -top-3" rotation="-3deg" />
        <h1 className="text-3xl mb-1 font-hand relative z-10">Spel Voltooid!</h1>
        <p className="text-muted-foreground">{gameState.course.name}</p>
      </div>

      <div className="flex-1 max-w-2xl w-full mx-auto space-y-4">
        <Podium
          topThree={players.slice(0, 3).map((p) => ({ name: p.name, score: p.total, id: p.id }))}
        />

        <div className="space-y-2">
          <h2 className="text-lg font-hand">Eindscores</h2>
          <div className="space-y-2">
            {players.map((player) => (
              <div key={player.id} className="flex items-center justify-between rounded-lg border border-border/60 bg-card px-3 py-3 shadow-paper-sm">
                <div className="flex items-center gap-3">
                  <GolfBall color={COLOR_HEX[player.selectedColor]} />
                  <span className="text-lg">{player.name}</span>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-hand">{player.total}</div>
                  <div className="text-sm text-muted-foreground">
                    {player.total - totalPar >= 0 ? '+' : ''}
                    {player.total - totalPar}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <h2 className="text-lg font-hand">Referentie</h2>
          <div className="rounded-lg bg-card/70 border border-border/60 shadow-paper-sm overflow-hidden">
            <div className="torn-edge h-2" />
            <div className="text-sm text-muted-foreground space-y-1 px-3 py-2">
              {SCORE_BANDS.map((band, index) => (
                <div
                  key={index}
                  className="flex justify-between py-1.5 border-b border-border/50 last:border-0"
                >
                  <span>{band.max === null ? `>${band.min}` : `${band.min} - ${band.max}`}</span>
                  <span className="text-right">{band.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <Button onClick={share} className="w-full" size="lg">
            <Share2 className="mr-2 h-5 w-5" />
            Delen
          </Button>
          <div className="grid grid-cols-2 gap-3">
            <Button onClick={replay} variant="outline" className="w-full bg-card shadow-sm">
              <RotateCcw className="mr-2 h-4 w-4" />
              Opnieuw
            </Button>
            <Button onClick={() => navigate('/leaderboard')} variant="outline" className="w-full bg-card shadow-sm">
              <Trophy className="mr-2 h-4 w-4" />
              Klassement
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}